import { useState, useEffect, useRef } from 'react'
import { HardDrive, Folder, File, Download, Copy, ChevronRight, ChevronDown, AlertCircle, Loader, Trash2, Search, X } from 'lucide-react'
import Modal from './Modal'
import { getAPIBaseUrl } from '../../environment'
import { useAccounts } from '../../hooks'
import './S3BrowserModal.css'

const API_BASE_URL = getAPIBaseUrl()

function S3BrowserModal({ isOpen, onClose, onOpenSettings, project }) {
  const { accounts } = useAccounts()
  const [buckets, setBuckets] = useState([])
  const [selectedBucket, setSelectedBucket] = useState('')
  const [files, setFiles] = useState([])
  const [selectedFile, setSelectedFile] = useState(null)
  const [currentPath, setCurrentPath] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Show all buckets toggle (default: false - only show account bucket)
  const [showAllBuckets, setShowAllBuckets] = useState(false)

  // Context menu state
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, folder: null, file: null, type: null })
  const contextMenuRef = useRef(null)

  // Delete confirmation state
  const [deleteConfirmation, setDeleteConfirmation] = useState({ visible: false, folder: null, file: null, type: null, deleting: false })

  // Search state
  const [searchQuery, setSearchQuery] = useState('')

  // File content preview state
  const [fileContent, setFileContent] = useState(null)
  const [loadingContent, setLoadingContent] = useState(false)

  // Resizable panels state
  const [fileTreeWidth, setFileTreeWidth] = useState(400)
  const [isResizing, setIsResizing] = useState(false)
  const resizeRef = useRef(null)

  // Get account from already-loaded accounts (via useAccounts hook)
  const account = accounts.find(acc => acc.scoutAccountId === project?.account)

  // Get account's S3 bucket and prefix
  const accountBucket = account?.s3Bucket || null
  const accountId = account?.scoutAccountId || null
  const accountPrefix = accountId ? `${accountId}/` : ''

  // Filter buckets based on showAllBuckets toggle
  const filteredBuckets = showAllBuckets || !accountBucket
    ? buckets
    : buckets.filter(bucket => bucket.name === accountBucket)

  // Load buckets when modal opens and reset showAllBuckets
  useEffect(() => {
    if (isOpen) {
      setShowAllBuckets(false)
      loadBuckets()
    }
  }, [isOpen])

  // Auto-select bucket when account and buckets are loaded
  useEffect(() => {
    if (buckets.length > 0) {
      if (accountBucket && buckets.some(b => b.name === accountBucket)) {
        setSelectedBucket(accountBucket)
      } else if (!selectedBucket) {
        setSelectedBucket(buckets[0].name)
      }
    }
  }, [accountBucket, buckets])

  // Load files when bucket changes
  useEffect(() => {
    if (selectedBucket) {
      setCurrentPath([]) // Reset path when bucket changes
      loadFiles(accountPrefix)
    }
  }, [selectedBucket, accountPrefix])

  const loadBuckets = async () => {
    setLoading(true)
    setError(null)
    try {
      const token = localStorage.getItem('modernizeit_token')
      const response = await fetch(`${API_BASE_URL}/s3/list-buckets`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        const errorMessage = errorData.detail || 'Failed to load buckets'

        // Check if it's a credentials error
        if (errorMessage.includes('credentials not configured') || errorMessage.includes('AWS credentials')) {
          throw new Error('CREDENTIALS_NOT_CONFIGURED')
        }

        throw new Error(errorMessage)
      }

      const data = await response.json()
      setBuckets(data.buckets || [])
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const loadFiles = async (prefix) => {
    if (!selectedBucket) {
      return
    }

    setLoading(true)
    setError(null)
    try {
      const token = localStorage.getItem('modernizeit_token')
      const response = await fetch(`${API_BASE_URL}/s3/list-files`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          bucket_name: selectedBucket,
          prefix: prefix,
          max_keys: 1000
        })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        const errorMessage = errorData.detail || 'Failed to load files'

        // Check if it's a credentials error
        if (errorMessage.includes('credentials not configured') || errorMessage.includes('AWS credentials')) {
          throw new Error('CREDENTIALS_NOT_CONFIGURED')
        }

        throw new Error(errorMessage)
      }

      const data = await response.json()
      setFiles(data.files || [])
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleFolderClick = (folderName) => {
    // Navigate into the folder
    setCurrentPath([...currentPath, folderName])
  }

  const handleFileClick = (file) => {
    setSelectedFile(file)
    loadFileContent(file)
  }

  const loadFileContent = async (file) => {
    const fileName = file.key.split('/').pop()
    const extension = fileName.split('.').pop().toLowerCase()
    const previewableExtensions = ['json', 'txt', 'md', 'log', 'csv', 'xml', 'html', 'css', 'js', 'py', 'java', 'cbl', 'cob']

    // Only load content for previewable file types and files under 5MB
    if (!previewableExtensions.includes(extension) || file.size > 5242880) {
      setFileContent(null)
      return
    }

    setLoadingContent(true)
    setFileContent(null)

    try {
      const token = localStorage.getItem('modernizeit_token')
      const response = await fetch(`${API_BASE_URL}/s3/download?bucket=${selectedBucket}&key=${encodeURIComponent(file.key)}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        throw new Error('Failed to load file content')
      }

      const text = await response.text()
      setFileContent({ content: text, extension })
    } catch (err) {
      console.error('Failed to load file content:', err)
      setFileContent({ error: err.message })
    } finally {
      setLoadingContent(false)
    }
  }

  const handleDownload = async (file) => {
    try {
      const token = localStorage.getItem('modernizeit_token')
      const response = await fetch(`${API_BASE_URL}/s3/download?bucket=${selectedBucket}&key=${encodeURIComponent(file.key)}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        const errorMessage = errorData.detail || 'Failed to download file'

        // Check if it's a credentials error
        if (errorMessage.includes('credentials not configured') || errorMessage.includes('AWS credentials')) {
          setError('CREDENTIALS_NOT_CONFIGURED')
          return
        }

        throw new Error(errorMessage)
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = file.key.split('/').pop()
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (err) {
      setError(`Download failed: ${err.message}`)
    }
  }

  const handleCopyPath = (file) => {
    const s3Uri = `s3://${selectedBucket}/${file.key}`
    navigator.clipboard.writeText(s3Uri)
    // TODO: Show toast notification
  }

  // Context menu handlers
  const handleFolderContextMenu = (e, folderPath) => {
    e.preventDefault()
    e.stopPropagation()
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      folder: folderPath,
      file: null,
      type: 'folder'
    })
  }

  const handleFileContextMenu = (e, file) => {
    e.preventDefault()
    e.stopPropagation()
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      folder: null,
      file: file,
      type: 'file'
    })
  }

  const handleDelete = async () => {
    const { folder, file, type } = deleteConfirmation

    setDeleteConfirmation(prev => ({ ...prev, deleting: true }))

    try {
      const token = localStorage.getItem('modernizeit_token')

      if (type === 'folder') {
        // Delete folder
        const prefix = folder.endsWith('/') ? folder : `${folder}/`

        const response = await fetch(`${API_BASE_URL}/s3/delete-folder`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            bucket_name: selectedBucket,
            prefix: prefix
          })
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.detail || 'Failed to delete folder')
        }

        // Clear selection if deleted folder contained the selected file
        if (selectedFile?.key.startsWith(prefix)) {
          setSelectedFile(null)
        }
      } else if (type === 'file') {
        // Delete file
        const response = await fetch(`${API_BASE_URL}/s3/delete-file`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            bucket_name: selectedBucket,
            key: file.key
          })
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.detail || 'Failed to delete file')
        }

        // Clear selection if deleted file was selected
        if (selectedFile?.key === file.key) {
          setSelectedFile(null)
        }
      }

      // Reload files after successful delete
      await loadFiles('')

      // Close confirmation modal
      setDeleteConfirmation({ visible: false, folder: null, file: null, type: null, deleting: false })
    } catch (err) {
      setError(`Delete failed: ${err.message}`)
      setDeleteConfirmation(prev => ({ ...prev, deleting: false }))
    }
  }

  // Close context menu on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target)) {
        setContextMenu({ visible: false, x: 0, y: 0, folder: null, file: null, type: null })
      }
    }

    if (contextMenu.visible) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [contextMenu.visible])

  // Handle panel resizing
  const handleMouseDown = (e) => {
    e.preventDefault()
    setIsResizing(true)
  }

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return

      const container = resizeRef.current?.parentElement
      if (!container) return

      const containerRect = container.getBoundingClientRect()
      const newWidth = e.clientX - containerRect.left

      // Constrain width between 250px and 70% of container width
      const minWidth = 250
      const maxWidth = containerRect.width * 0.7

      if (newWidth >= minWidth && newWidth <= maxWidth) {
        setFileTreeWidth(newWidth)
      }
    }

    const handleMouseUp = () => {
      setIsResizing(false)
    }

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)

      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [isResizing])

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleString()
  }

  // Get folders and files at the current path level
  const getCurrentLevelItems = () => {
    const folders = new Set()
    const filesAtLevel = []

    // Build the current prefix from currentPath
    const currentPrefix = currentPath.length > 0 ? currentPath.join('/') + '/' : ''

    files.forEach(file => {
      // Remove the current prefix to get the relative path
      if (!file.key.startsWith(currentPrefix)) return

      const relativePath = file.key.substring(currentPrefix.length)
      const parts = relativePath.split('/')

      if (parts.length === 1) {
        // It's a file at this level
        filesAtLevel.push(file)
      } else if (parts.length > 1 && parts[0]) {
        // It's in a subfolder - add the folder name
        folders.add(parts[0])
      }
    })

    return {
      folders: Array.from(folders).sort(),
      files: filesAtLevel.sort((a, b) => {
        const aName = a.key.split('/').pop()
        const bName = b.key.split('/').pop()
        return aName.localeCompare(bName)
      })
    }
  }

  const renderFileTree = () => {
    const { folders, files: filesAtLevel } = getCurrentLevelItems()
    const items = []

    // Build the current full path for context menu
    const currentPrefix = currentPath.length > 0 ? currentPath.join('/') + '/' : ''

    // Render folders
    folders.forEach(folderName => {
      const folderPath = currentPrefix + folderName

      items.push(
        <div key={folderPath} className="file-tree-item">
          <button
            className="file-tree-folder"
            onClick={() => handleFolderClick(folderName)}
            onContextMenu={(e) => handleFolderContextMenu(e, folderPath)}
          >
            <ChevronRight size={16} />
            <Folder size={16} />
            <span>{folderName}</span>
          </button>
        </div>
      )
    })

    // Render files at this level
    filesAtLevel
      .filter(file => {
        // Apply search filter
        if (!searchQuery) return true
        const fileName = file.key.toLowerCase()
        return fileName.includes(searchQuery.toLowerCase())
      })
      .forEach(file => {
        items.push(
          <button
            key={file.key}
            className={`file-tree-file ${selectedFile?.key === file.key ? 'selected' : ''}`}
            onClick={() => handleFileClick(file)}
            onContextMenu={(e) => handleFileContextMenu(e, file)}
          >
            <File size={16} />
            <span>{file.key.split('/').pop()}</span>
          </button>
        )
      })

    return items
  }

  const renderPreview = () => {
    if (!selectedFile) {
      return (
        <div className="preview-empty">
          <HardDrive size={48} />
          <p>Select a file to preview</p>
        </div>
      )
    }

    const fileName = selectedFile.key.split('/').pop()
    const extension = fileName.split('.').pop().toLowerCase()

    return (
      <div className="preview-content">
        <div className="preview-header">
          <File size={20} />
          <div className="preview-title">
            <div className="preview-filename">{fileName}</div>
            <div className="preview-meta">
              {formatFileSize(selectedFile.size)} • {formatDate(selectedFile.last_modified)}
            </div>
          </div>
        </div>

        <div className="preview-actions">
          <button className="btn-primary" onClick={() => handleDownload(selectedFile)}>
            <Download size={16} />
            <span>Download</span>
          </button>
          <button className="btn-secondary" onClick={() => handleCopyPath(selectedFile)}>
            <Copy size={16} />
            <span>Copy Path</span>
          </button>
        </div>

        {loadingContent ? (
          <div className="preview-loading">
            <Loader size={24} className="spinner" />
            <span>Loading content...</span>
          </div>
        ) : fileContent?.error ? (
          <div className="preview-notice error">
            Failed to load file content: {fileContent.error}
          </div>
        ) : fileContent?.content ? (
          <div className="preview-text-content">
            {extension === 'json' ? (
              <pre className="preview-json">{JSON.stringify(JSON.parse(fileContent.content), null, 2)}</pre>
            ) : (
              <pre className="preview-plaintext">{fileContent.content}</pre>
            )}
          </div>
        ) : selectedFile.size > 5242880 ? (
          <div className="preview-notice">
            File too large to preview ({formatFileSize(selectedFile.size)}). Maximum preview size is 5 MB. Use Download button.
          </div>
        ) : !['json', 'txt', 'md', 'log', 'csv', 'xml', 'html', 'css', 'js', 'py', 'java', 'cbl', 'cob'].includes(extension) ? (
          <div className="preview-notice">
            Preview not available for .{extension} files. Use Download button.
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="S3 Browser" size="xlarge">
      <div className="s3-browser">
        {/* Bucket selector */}
        <div className="s3-header">
          <label>
            <HardDrive size={16} />
            <span>Bucket:</span>
          </label>
          <select
            value={selectedBucket}
            onChange={(e) => setSelectedBucket(e.target.value)}
            disabled={loading || filteredBuckets.length === 0}
          >
            {filteredBuckets.length === 0 ? (
              <option>No buckets available</option>
            ) : (
              filteredBuckets.map(bucket => (
                <option key={bucket.name} value={bucket.name}>
                  {bucket.name}
                </option>
              ))
            )}
          </select>

          {/* Show all buckets toggle - only visible if account has an assigned bucket */}
          {accountBucket && (
            <label className="s3-checkbox-label">
              <input
                type="checkbox"
                checked={showAllBuckets}
                onChange={(e) => setShowAllBuckets(e.target.checked)}
              />
              <span>Show all S3 buckets</span>
            </label>
          )}
        </div>

        {/* Error display */}
        {error && (
          <div className="s3-error">
            <AlertCircle size={16} />
            {error === 'CREDENTIALS_NOT_CONFIGURED' ? (
              <div className="error-content">
                <span>AWS credentials not configured. Please configure your AWS settings to use the S3 browser.</span>
                <button
                  className="btn-settings-link"
                  onClick={() => {
                    onClose()
                    onOpenSettings && onOpenSettings()
                  }}
                >
                  Open Settings
                </button>
              </div>
            ) : (
              <span>{error}</span>
            )}
          </div>
        )}

        {/* Main content */}
        <div className="s3-content" ref={resizeRef}>
          {/* File tree (left panel) */}
          <div className="s3-file-tree" style={{ width: `${fileTreeWidth}px` }}>
            {/* Breadcrumb navigation */}
            {currentPath.length > 0 && (
              <div className="s3-breadcrumb">
                <button
                  className="breadcrumb-item"
                  onClick={() => setCurrentPath([])}
                >
                  <HardDrive size={14} />
                  <span>Root</span>
                </button>
                {currentPath.map((folder, index) => (
                  <div key={index} className="breadcrumb-separator">
                    <ChevronRight size={14} />
                    <button
                      className="breadcrumb-item"
                      onClick={() => setCurrentPath(currentPath.slice(0, index + 1))}
                    >
                      {folder}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Search input */}
            {files.length > 0 && (
              <div className="s3-search">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Search files..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    className="search-clear"
                    onClick={() => setSearchQuery('')}
                    title="Clear search"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            )}

            {loading ? (
              <div className="loading-state">
                <Loader size={24} className="spinner" />
                <span>Loading...</span>
              </div>
            ) : files.length === 0 ? (
              <div className="empty-state">
                <Folder size={48} />
                <p>No files found</p>
              </div>
            ) : (
              <div className="file-tree">
                {renderFileTree()}
              </div>
            )}
          </div>

          {/* Resize handle */}
          <div
            className="s3-resize-handle"
            onMouseDown={handleMouseDown}
            style={{ cursor: isResizing ? 'col-resize' : 'col-resize' }}
          />

          {/* Preview panel (right panel) */}
          <div className="s3-preview">
            {renderPreview()}
          </div>
        </div>
      </div>

      {/* Context Menu */}
      {contextMenu.visible && (
        <div
          ref={contextMenuRef}
          className="s3-context-menu"
          style={{
            position: 'fixed',
            top: `${contextMenu.y}px`,
            left: `${contextMenu.x}px`,
            zIndex: 10000
          }}
        >
          <button
            className="context-menu-item danger"
            onClick={() => {
              setDeleteConfirmation({
                visible: true,
                folder: contextMenu.folder,
                file: contextMenu.file,
                type: contextMenu.type,
                deleting: false
              })
              setContextMenu({ visible: false, x: 0, y: 0, folder: null, file: null, type: null })
            }}
          >
            <Trash2 size={16} />
            <span>Delete {contextMenu.type === 'folder' ? 'Folder' : 'File'}</span>
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation.visible && (
        <div className="s3-delete-confirmation-overlay">
          <div className="s3-delete-confirmation">
            <h3>Delete {deleteConfirmation.type === 'folder' ? 'Folder' : 'File'}?</h3>
            <p className="warning-text">
              <AlertCircle size={16} />
              You are about to delete:
            </p>
            <div className="folder-path">
              {deleteConfirmation.type === 'folder' ? <Folder size={16} /> : <File size={16} />}
              <code>
                {deleteConfirmation.type === 'folder'
                  ? deleteConfirmation.folder
                  : deleteConfirmation.file?.key}
              </code>
            </div>
            <p className="warning-text">
              {deleteConfirmation.type === 'folder' ? (
                <>This will permanently delete this folder and <strong>all files inside it</strong> from S3. This action cannot be undone.</>
              ) : (
                <>This will permanently delete this file from S3. This action cannot be undone.</>
              )}
            </p>
            <div className="confirmation-actions">
              <button
                className="btn-cancel"
                onClick={() => setDeleteConfirmation({ visible: false, folder: null, file: null, type: null, deleting: false })}
                disabled={deleteConfirmation.deleting}
              >
                Cancel
              </button>
              <button
                className="btn-delete"
                onClick={handleDelete}
                disabled={deleteConfirmation.deleting}
              >
                {deleteConfirmation.deleting ? (
                  <>
                    <Loader size={16} className="spinner" />
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    <span>Delete {deleteConfirmation.type === 'folder' ? 'Folder' : 'File'}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  )
}

export default S3BrowserModal
